import Swal from "sweetalert2";
import { getUiErrorMessage } from "../services/utils";

export const DAYS_LABEL = ["Lunedì", "Martedì", "Mercoledì", "Giovedì", "Venerdì", "Sabato", "Domenica"];
export const DAY_MIN = 24 * 60;

export const uid = () =>
  (globalThis.crypto?.randomUUID?.() ? globalThis.crypto.randomUUID() : String(Date.now() + Math.random()));

// -----------------------------
// TIME
// -----------------------------
export function toMin(hhmm) {
  if (!hhmm || typeof hhmm !== "string" || !hhmm.includes(":")) return null;
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  if (h === 24 && m === 0) return DAY_MIN; // 24:00
  if (h < 0 || h > 23 || m < 0 || m > 59) return null;
  return h * 60 + m;
}

export function toDayTime(min) {
  if (min === 1440) return "24:00";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

export function isCrossMidnight(start, end) {
  const s = toMin(start);
  const e = toMin(end);
  if (s == null || e == null) return false;
  return e < s;
}

export function nextDayLabel(dayLabel) {
  const idx = DAYS_LABEL.indexOf(dayLabel);
  return DAYS_LABEL[(idx + 1) % 7];
}

export function normalizeBreaks(breaks) {
  return (breaks || [])
    .filter((b) => b?.start_at && b?.end_at)
    .map((b) => ({
      start_at: b.start_at,
      end_at: b.end_at,
      is_visible: b.is_visible ?? true,
      confirmed: b.confirmed ?? true,
    }))
    .sort((a, b) => (a.start_at || "").localeCompare(b.start_at || ""));
}

// -----------------------------
// OVERLAP
// -----------------------------
function overlaps(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd;
}

function validateNoOverlapInDay(dayLabel, daySlots, slotToAdd, ignoreGroupId = null) {
  const s = toMin(slotToAdd.workStartTime);
  const e = toMin(slotToAdd.workEndTime);
  if (s == null || e == null) throw new Error("Orari non validi.");

  for (const ex of daySlots || []) {
    if (ignoreGroupId && ex.groupId === ignoreGroupId) continue;

    const es = toMin(ex.workStartTime);
    const ee = toMin(ex.workEndTime);
    if (es == null || ee == null) continue;

    if (overlaps(s, e, es, ee)) {
      throw new Error(`Sovrapposizione su ${dayLabel} nella fascia ${ex.workStartTime}-${ex.workEndTime}.`);
    }
  }
}

function sortDaySlots(slots) {
  return [...slots].sort((a, b) => (toMin(a.workStartTime) ?? 0) - (toMin(b.workStartTime) ?? 0));
}

// -----------------------------
// BREAK SPLIT
// -----------------------------
function splitBreaksAcrossMidnight(slotStart, slotEnd, breaks) {
  const start = toMin(slotStart);
  const end = toMin(slotEnd);
  const endAbs = end + 1440;

  const b1 = [];
  const b2 = [];

  for (const br of normalizeBreaks(breaks)) {
    let bs = toMin(br.start_at);
    let be = toMin(br.end_at);
    if (bs == null || be == null) continue;

    if (bs < start) bs += 1440;
    if (be < start) be += 1440;
    if (be < bs) be += 1440;

    const cStart = Math.max(bs, start);
    const cEnd = Math.min(be, endAbs);
    if (cEnd <= cStart) continue;

    // day1
    const p1s = Math.max(cStart, start);
    const p1e = Math.min(cEnd, 1440);
    if (p1e > p1s) b1.push({ ...br, start_at: toDayTime(p1s), end_at: toDayTime(p1e) });

    // day2
    const p2s = Math.max(cStart, 1440);
    const p2e = Math.min(cEnd, endAbs);
    if (p2e > p2s) b2.push({ ...br, start_at: toDayTime(p2s - 1440), end_at: toDayTime(p2e - 1440) });
  }

  return { b1: normalizeBreaks(b1), b2: normalizeBreaks(b2) };
}

// -----------------------------
// INTERNAL HELPERS
// -----------------------------
function ensureCustomDay(nextDays, dayLabel) {
  const cfg = nextDays[dayLabel];
  if (cfg?.type === "custom") return cfg;
  nextDays[dayLabel] = { type: "custom", workingHourId: null, workingHourObj: null, customSlots: [] };
  return nextDays[dayLabel];
}

function removeGroupEverywhere(daysData, groupId) {
  const next = { ...daysData };
  for (const day of Object.keys(next)) {
    const cfg = next[day];
    if (cfg?.type !== "custom") continue;
    const filtered = (cfg.customSlots || []).filter((s) => s.groupId !== groupId);
    if (filtered.length === 0) delete next[day];
    else next[day] = { ...cfg, customSlots: filtered };
  }
  return next;
}

function buildPieces(anchorDay, rawSlot, groupId) {
  const slot = {
    workStartTime: rawSlot.workStartTime,
    workEndTime: rawSlot.workEndTime,
    breaks: normalizeBreaks(rawSlot.breaks || []),
  };

  // no split
  if (!isCrossMidnight(slot.workStartTime, slot.workEndTime)) {
    return [
      {
        dayLabel: anchorDay,
        slot: {
          id: uid(),
          groupId,
          anchorDay,
          workStartTime: slot.workStartTime,
          workEndTime: slot.workEndTime,
          breaks: slot.breaks,
          spillFrom: null,
        },
      },
    ];
  }

  // split
  const next = nextDayLabel(anchorDay);
  const { b1, b2 } = splitBreaksAcrossMidnight(slot.workStartTime, slot.workEndTime, slot.breaks);

  return [
    {
      dayLabel: anchorDay,
      slot: {
        id: uid(),
        groupId,
        anchorDay,
        workStartTime: slot.workStartTime,
        workEndTime: "24:00",
        breaks: b1,
        spillFrom: null,
      },
    },
    {
      dayLabel: next,
      slot: {
        id: uid(),
        groupId,
        anchorDay,
        workStartTime: "00:00",
        workEndTime: slot.workEndTime,
        breaks: b2,
        spillFrom: anchorDay,
      },
    },
  ];
}

// -----------------------------
// PUBLIC API (USED BY BuildWeekShiftPreview)
// -----------------------------

/**
 * Upsert “group”: crea/modifica una fascia unica (anche se split).
 * editingGroupId: se presente, sostituisce quel gruppo.
 */
export function tryUpsertCustomSlot(daysData, anchorDay, rawSlot, editingGroupId = null) {
  try {
    const groupId = editingGroupId || uid();

    // base: rimuovo il gruppo che sto editando ovunque (così niente overlap falso)
    let next = editingGroupId ? removeGroupEverywhere(daysData, editingGroupId) : { ...daysData };

    const pieces = buildPieces(anchorDay, rawSlot, groupId);

    // validate overlap giorno-per-giorno
    for (const p of pieces) {
      const cfg = next[p.dayLabel];
      const existing = cfg?.type === "custom" ? (cfg.customSlots || []) : [];
      validateNoOverlapInDay(p.dayLabel, existing, p.slot, null);
    }

    // insert
    for (const p of pieces) {
      const cfg = ensureCustomDay(next, p.dayLabel);
      cfg.customSlots = sortDaySlots([...(cfg.customSlots || []), p.slot]);
    }

    return { ok: true, data: next };
  } catch (error) {
    return { ok: false, error };
  }
}

/**
 * Restituisce: lista “pezzi” visibili nella modale quando apri un giorno.
 * UX: apri Martedì -> vedi anche il pezzo che arriva da Lunedì (stesso groupId).
 */
export function getVisiblePiecesForDay(daysData, dayLabel) {
  const visible = [];
  const dayIdx = DAYS_LABEL.indexOf(dayLabel);

  // prendo tutti gli slot custom del giorno
  const cfg = daysData?.[dayLabel];
  const todaySlots = cfg?.type === "custom" ? (cfg.customSlots || []) : [];

  // groupId presenti oggi
  const groupIds = new Set(todaySlots.map((s) => s.groupId).filter(Boolean));

  // in più: includo i gruppi “collegati” (spillFrom = dayLabel) sul giorno successivo
  const nextDay = nextDayLabel(dayLabel);
  const nextCfg = daysData?.[nextDay];
  const nextSlots = nextCfg?.type === "custom" ? (nextCfg.customSlots || []) : [];
  nextSlots.forEach((s) => {
    if (s.spillFrom === dayLabel && s.groupId) groupIds.add(s.groupId);
  });

  // raccolgo TUTTI i pezzi di quei gruppi, anche su altri giorni (ma in pratica max 2 giorni)
  for (const dl of DAYS_LABEL) {
    const c = daysData?.[dl];
    const slots = c?.type === "custom" ? (c.customSlots || []) : [];
    for (const s of slots) {
      if (s.groupId && groupIds.has(s.groupId)) {
        visible.push({
          ...s,
          __dayLabel: dl,
        });
      }
    }
  }

  // ordino per giorno (rispetto alla settimana) e poi per ora
  visible.sort((a, b) => {
    const da = DAYS_LABEL.indexOf(a.__dayLabel);
    const db = DAYS_LABEL.indexOf(b.__dayLabel);
    if (da !== db) return da - db;
    return (toMin(a.workStartTime) ?? 0) - (toMin(b.workStartTime) ?? 0);
  });

  return { visible };
}

/**
 * Ritorna l’oggetto “originale” da mettere nell’editor (start/end reali + breaks).
 * Se split, ricostruisce end dal pezzo del giorno successivo.
 */
export function getEditingOriginalByGroup(daysData, groupId) {
  if (!groupId) return null;

  // trova tutti i pezzi di quel groupId
  const pieces = [];
  for (const dl of DAYS_LABEL) {
    const cfg = daysData?.[dl];
    if (cfg?.type !== "custom") continue;
    for (const s of cfg.customSlots || []) {
      if (s.groupId === groupId) pieces.push({ ...s, __dayLabel: dl });
    }
  }
  if (!pieces.length) return null;

  // anchorDay: il valore scritto sugli slot
  const anchorDay = pieces[0].anchorDay || pieces.find((p) => p.__dayLabel)?.__dayLabel || null;

  // pezzo anchor (quello con start != 00:00 o spillFrom null)
  const anchorPiece =
    pieces.find((p) => p.__dayLabel === anchorDay) ||
    pieces.sort((a, b) => (toMin(a.workStartTime) ?? 0) - (toMin(b.workStartTime) ?? 0))[0];

  // se esiste un pezzo sul giorno successivo con spillFrom=anchorDay e start 00:00 => è l’end reale
  const spillPiece = pieces.find((p) => p.spillFrom === anchorDay && p.workStartTime === "00:00");

  const workStartTime = anchorPiece.workStartTime;
  const workEndTime = spillPiece ? spillPiece.workEndTime : anchorPiece.workEndTime;

  // breaks: unione semplice (già split per giorno). Per l’editor puoi mostrarle tutte insieme:
  const breaks = normalizeBreaks([
    ...(anchorPiece.breaks || []),
    ...(spillPiece?.breaks || []),
  ]);

  return { anchorDay, groupId, workStartTime, workEndTime, breaks };
}

export function deleteSlotGroup(daysData, groupId) {
  if (!groupId) return daysData;
  return removeGroupEverywhere(daysData, groupId);
}

export function clearDay(daysData, dayLabel) {
  const next = { ...daysData };

  // 1) rimuovi config del giorno
  delete next[dayLabel];

  // 2) rimuovi i pezzi spillati sul giorno successivo che arrivano da dayLabel
  const nextDay = nextDayLabel(dayLabel);
  const cfgNext = next[nextDay];
  if (cfgNext?.type === "custom") {
    const filtered = (cfgNext.customSlots || []).filter((s) => s.spillFrom !== dayLabel);
    if (filtered.length === 0) delete next[nextDay];
    else next[nextDay] = { ...cfgNext, customSlots: filtered };
  }

  return next;
}

export function applyWorkingHourToDay(daysData, dayLabel, workingHourObj) {
  const next = clearDay(daysData, dayLabel);
  next[dayLabel] = {
    type: "working_hour",
    workingHourId: workingHourObj.id,
    workingHourObj,
    customSlots: [],
  };
  return next;
}


export function tryEditExistingGroup(daysData, editingGroupId, rawSlotPatch) {
  try {
    if (!editingGroupId) throw new Error("Missing editingGroupId");

    // trova tutti i pezzi con groupId = editingGroupId
    const matches = [];
    for (const [dayLabel, cfg] of Object.entries(daysData || {})) {
      if (cfg?.type !== "custom") continue;
      for (const s of cfg.customSlots || []) {
        if (s.groupId === editingGroupId) {
          matches.push({ dayLabel, slot: s });
        }
      }
    }

    if (!matches.length) {
      throw new Error("Group not found in daysData");
    }

    // qui non facciamo split/merge: stessa quantità di pezzi
    // se buildPieces per rawSlotPatch produce un numero diverso -> errore
    // (per non complicare)
    // Quindi applichiamo patch agli slot esistenti in place (stesso count)
    const next = { ...daysData };
    for (const m of matches) {
      const cfg = next[m.dayLabel];
      cfg.customSlots = (cfg.customSlots || []).map((s) => {
        if (s.groupId !== editingGroupId) return s;

        // manteniamo backendId e groupId
        return {
          ...s,
          workStartTime: rawSlotPatch.workStartTime,
          workEndTime: rawSlotPatch.workEndTime,
          breaks: rawSlotPatch.breaks || [],
        };
      });

      // riordina
      cfg.customSlots = cfg.customSlots.slice().sort((a, b) => (a.workStartTime || "").localeCompare(b.workStartTime || ""));
    }

    return { ok: true, data: next, backendIds: matches.map(m => m.slot.backendId).filter(Boolean) };
  } catch (error) {
    return { ok: false, error };
  }
}




// utils/weekDayEditLogic.jsx
// Funzioni “core” per edit di un day-slot con possibilità di estensione al giorno successivo
// Backend: giorni separati, no cross-midnight reale
// UI: modale ancorata al giorno cliccato; l’utente può inserire end < start => estendi al giorno dopo
// Linking “simple” (senza groupId backend): A.end_at==="24:00" + next.start_at==="00:00" (univoco)


// API
import {
  addWeeklyWorkingHourSlot,
  patchUpdateWeeklyWorkingHourSlot,
  deleteWeeklyWorkingHoursSlot,
  deleteTimeSlotCompany,
  updateTimeSlot,
  createBreakTimeSlot,
  updateBreakTimeSlot,
  deleteBreakTimeSlot,
} from "../services/api_working_hours";

// -----------------------
// Helpers time strings
// -----------------------
export const uiTime = (t) => (t ? String(t).slice(0, 5) : "");
export const apiTime = (t) => (t ? String(t).slice(0, 5) : "");

export const dayLabelFromWeekDay = (weekDay) => DAYS_LABEL[(weekDay || 1) - 1] || "";

// breaks mapping (UI <-> API) = stringhe
export const mapApiBreaksToUi = (breaks = []) =>
  (breaks || []).map((b) => ({
    id: b.id,
    start_at: uiTime(b.start_at),
    end_at: uiTime(b.end_at),
    is_visible: b.is_visible ?? true,
  }));

export const normalizeBreaksForApi = (breaks = []) =>
  (breaks || []).map((b) => ({
    id: b.id,
    start_at: apiTime(b.start_at),
    end_at: apiTime(b.end_at),
    is_visible: b.is_visible ?? true,
  }));

// -----------------------
// Breaks sync CRUD
// -----------------------
export async function syncBreaks(token, timeSlotId, prevBreaksUi = [], nextBreaksUi = []) {
  const prev = prevBreaksUi || [];
  const next = nextBreaksUi || [];

  const prevById = new Map(prev.filter((b) => b?.id).map((b) => [b.id, b]));
  const nextById = new Map(next.filter((b) => b?.id).map((b) => [b.id, b]));

  // DELETE removed
  for (const [id] of prevById.entries()) {
    if (!nextById.has(id)) {
      await deleteBreakTimeSlot(token, id);
    }
  }

  // UPDATE existing
  for (const [id, nb] of nextById.entries()) {
    const pb = prevById.get(id);
    const changed =
      !pb ||
      uiTime(pb.start_at) !== uiTime(nb.start_at) ||
      uiTime(pb.end_at) !== uiTime(nb.end_at) ||
      (pb.is_visible ?? true) !== (nb.is_visible ?? true);

    if (changed) {
      await updateBreakTimeSlot(
        token,
        id,
        {
          start_at: apiTime(nb.start_at),
          end_at: apiTime(nb.end_at),
          is_visible: nb.is_visible ?? true,
        },
        timeSlotId
      );
    }
  }

  // CREATE new (no id)
  const toCreate = next.filter((b) => !b?.id);
  for (const b of toCreate) {
    await createBreakTimeSlot(
      token,
      {
        start_at: apiTime(b.start_at),
        end_at: apiTime(b.end_at),
        is_visible: b.is_visible ?? true,
      },
      timeSlotId
    );
  }
}

// -----------------------
// Split breaks across midnight (HH:MM strings)
// (riusa la tua logica: normalizeBreaks + isCrossMidnight + nextDayLabel)
// Qui implemento una split semplice e coerente con la tua normalizeBreaks
// -----------------------


// splits breaks from (start -> end next day) into day1/day2 breaks
export function splitBreaksAcrossMidnightStrings(slotStart, slotEnd, breaks) {
  const start = toMin(slotStart);
  const end = toMin(slotEnd);
  if (start == null || end == null) return { b1: [], b2: [] };

  const endAbs = end + 1440;
  const b1 = [];
  const b2 = [];

  for (const br of normalizeBreaks(breaks)) {
    let bs = toMin(br.start_at);
    let be = toMin(br.end_at);
    if (bs == null || be == null) continue;

    // portiamo la pausa nello stesso “timeline” assoluto del turno
    if (bs < start) bs += 1440;
    if (be < start) be += 1440;
    if (be < bs) be += 1440;

    const cStart = Math.max(bs, start);
    const cEnd = Math.min(be, endAbs);
    if (cEnd <= cStart) continue;

    // day1 overlap with [start,1440]
    const p1s = Math.max(cStart, start);
    const p1e = Math.min(cEnd, 1440);
    if (p1e > p1s) b1.push({ ...br, start_at: toDayTime(p1s), end_at: toDayTime(p1e) });

    // day2 overlap with [1440,endAbs]
    const p2s = Math.max(cStart, 1440);
    const p2e = Math.min(cEnd, endAbs);
    if (p2e > p2s) b2.push({ ...br, start_at: toDayTime(p2s - 1440), end_at: toDayTime(p2e - 1440) });
  }

  return { b1: normalizeBreaks(b1), b2: normalizeBreaks(b2) };
}

// -----------------------
// Find SBW for a day (weekly-working-hours-slot)
// -----------------------
export function getSbwForWeekDay(weeklyWorkingHour, weekDay) {
  return (weeklyWorkingHour?.slots_by_weekday || []).find((x) => x.week_day === weekDay) || null;
}

// Ensure SBW exists for a weekDay (POST if missing)
export async function ensureSbw(token, weeklyWorkingHourId, weekDay, currentSbw) {
  if (currentSbw?.id) return currentSbw;

  const created = await addWeeklyWorkingHourSlot(token, {
    week_day: weekDay,
    weekly_working_hour: weeklyWorkingHourId,
    working_hour: null,
    time_slots: [],
  });

  return created;
}

// -----------------------
// Linking rule (no groupId):
// anchor ends 24:00 and next day has exactly one slot with start 00:00
// optionally you can also require that next slot belongs to a custom day (working_hour null)
// -----------------------
export function findLinkedNextSlot({ weeklyWorkingHour, anchorWeekDay, anchorTimeSlotId }) {
  const anchorSbw = getSbwForWeekDay(weeklyWorkingHour, anchorWeekDay);
  if (!anchorSbw) return { nextTimeSlot: null, nextSbw: null };

  const anchorTs = (anchorSbw.time_slots || []).find((t) => t.id === anchorTimeSlotId) || null;
  if (!anchorTs) return { nextTimeSlot: null, nextSbw: null };

  if (uiTime(anchorTs.end_at) !== "24:00") return { nextTimeSlot: null, nextSbw: null };

  const nextWeekDay = anchorWeekDay === 7 ? 1 : anchorWeekDay + 1;
  const nextSbw = getSbwForWeekDay(weeklyWorkingHour, nextWeekDay);
  if (!nextSbw) return { nextTimeSlot: null, nextSbw: null };

  // ignore if nextSbw is working_hour day
  if (nextSbw.working_hour) return { nextTimeSlot: null, nextSbw: null };

  const candidates = (nextSbw.time_slots || []).filter((t) => uiTime(t.start_at) === "00:00");
  if (candidates.length !== 1) return { nextTimeSlot: null, nextSbw: null };

  return { nextTimeSlot: candidates[0], nextSbw };
}

// -----------------------
// Apply “custom” to SBW: detach working_hour if present
// -----------------------
export async function detachWorkingHourIfNeeded(token, sbw) {
  if (!sbw?.id) return sbw;
  if (!sbw.working_hour) return sbw;

  // Serializer: if time_slots provided -> working_hour becomes null
  const resp = await patchUpdateWeeklyWorkingHourSlot(token, sbw.id, {
    working_hour: null,
    time_slots: [],
  });
  return resp;
}

// -----------------------
// Create a NEW time slot on a given SBW using nested PATCH
// Returns updated SBW response
// -----------------------
export async function createTimeSlotOnSbw(token, sbwId, { start_at, end_at, breaks }) {
  const resp = await patchUpdateWeeklyWorkingHourSlot(token, sbwId, {
    working_hour: null,
    time_slots: [
      {
        start_at: apiTime(start_at),
        end_at: apiTime(end_at),
        breaks: (breaks || []).map((b) => ({
          start_at: apiTime(b.start_at),
          end_at: apiTime(b.end_at),
          is_visible: b.is_visible ?? true,
        })),
      },
    ],
  });
  return resp;
}

// -----------------------
// Save edit anchored on one slot, with possible extension to next day
// INPUT:
// - anchorWeekDay (1..7)
// - anchorSbw (may be null)
// - anchorTimeSlot (existing) or null for "create new"
// - rawSlot: {workStartTime, workEndTime, breaks: ui-breaks}
// BEHAVIOR:
// - If cross: ensure anchor ends 24:00 and next starts 00:00
// - Patch/update/delete/create the appropriate backend objects
// NOTE: breaks are synced with breaks CRUD for updateTimeSlot,
//       while for created slots via nested PATCH they are created inside request.
// -----------------------
export async function saveAnchoredTimeSlotEdit({
  token,
  weeklyWorkingHour,
  anchorWeekDay,
  anchorSbw,
  anchorTimeSlot, // {id,start_at,end_at,breaks}
  rawSlot, // {workStartTime, workEndTime, breaks}
  onSaved, // callback
}) {
  const start = apiTime(rawSlot?.workStartTime);
  const end = apiTime(rawSlot?.workEndTime);
  const breaksUi = rawSlot?.breaks || [];

  if (!start || !end) {
    await Swal.fire({ icon: "error", title: "Orari non validi" });
    return { ok: false };
  }

  const cross = isCrossMidnight(start, end);

  // Ensure anchor SBW exists if we need to write anything
  const ensuredAnchorSbw = await ensureSbw(token, weeklyWorkingHour.id, anchorWeekDay, anchorSbw);

  // If anchor SBW has working_hour, detach (we're editing custom)
  const anchorSbwCustom = await detachWorkingHourIfNeeded(token, ensuredAnchorSbw);

  // Linked next slot (if any) only meaningful when anchor exists and ends at 24:00 today
  const { nextTimeSlot, nextSbw } = anchorTimeSlot?.id
    ? findLinkedNextSlot({ weeklyWorkingHour, anchorWeekDay, anchorTimeSlotId: anchorTimeSlot.id })
    : { nextTimeSlot: null, nextSbw: null };

  // -----------------------------
  // CASE: NOT CROSS MIDNIGHT
  // -----------------------------
  if (!cross) {
    // Update anchor slot (must exist)
    if (!anchorTimeSlot?.id) {
      // create new single-day slot
      const resp = await createTimeSlotOnSbw(token, anchorSbwCustom.id, {
        start_at: start,
        end_at: end,
        breaks: normalizeBreaksForApi(breaksUi),
      });

      await onSaved?.();
      return { ok: true, updatedAnchorSbw: resp };
    }

    // PATCH time slot
    await updateTimeSlot(token, anchorTimeSlot.id, { start_at: start, end_at: end });

    // Sync breaks on anchor
    await syncBreaks(token, anchorTimeSlot.id, mapApiBreaksToUi(anchorTimeSlot.breaks || []), breaksUi);

    // If there was a linked next slot previously, delete it (we no longer cross)
    if (nextTimeSlot?.id) {
      await deleteTimeSlotCompany(token, nextTimeSlot.id);

      // If next SBW becomes empty -> delete next SBW
      const remaining = (nextSbw?.time_slots || []).filter((t) => t.id !== nextTimeSlot.id);
      if (remaining.length === 0 && nextSbw?.id) {
        await deleteWeeklyWorkingHoursSlot(token, nextSbw.id);
      }
    }

    await onSaved?.();
    return { ok: true };
  }

  // -----------------------------
  // CASE: CROSS MIDNIGHT
  // Need:
  //  - anchor: start -> 24:00
  //  - next: 00:00 -> end
  //  - breaks split across midnight
  // -----------------------------
  const { b1, b2 } = splitBreaksAcrossMidnightStrings(start, end, breaksUi);

  // 1) Anchor slot:
  if (!anchorTimeSlot?.id) {
    // Create anchor piece (start -> 24:00)
    const respA = await createTimeSlotOnSbw(token, anchorSbwCustom.id, {
      start_at: start,
      end_at: "24:00",
      breaks: normalizeBreaksForApi(b1),
    });

    // For next piece, create on next day
    const nextWeekDay = anchorWeekDay === 7 ? 1 : anchorWeekDay + 1;
    const nextSbwExisting = getSbwForWeekDay(weeklyWorkingHour, nextWeekDay);
    const ensuredNextSbw = await ensureSbw(token, weeklyWorkingHour.id, nextWeekDay, nextSbwExisting);
    const nextSbwCustom = await detachWorkingHourIfNeeded(token, ensuredNextSbw);

    const respB = await createTimeSlotOnSbw(token, nextSbwCustom.id, {
      start_at: "00:00",
      end_at: end,
      breaks: normalizeBreaksForApi(b2),
    });

    await onSaved?.();
    return { ok: true, updatedAnchorSbw: respA, updatedNextSbw: respB };
  }

  // Existing anchor slot => patch it to (start -> 24:00)
  // Optional: skip patch if unchanged
  const shouldPatchAnchor =
    uiTime(anchorTimeSlot.start_at) !== uiTime(start) || uiTime(anchorTimeSlot.end_at) !== "24:00";

  if (shouldPatchAnchor) {
    await updateTimeSlot(token, anchorTimeSlot.id, { start_at: start, end_at: "24:00" });
  }

  // Sync breaks on anchor with b1
  await syncBreaks(token, anchorTimeSlot.id, mapApiBreaksToUi(anchorTimeSlot.breaks || []), b1);

  // 2) Next slot:
  if (nextTimeSlot?.id) {
    // Patch next to (00:00 -> end)
    const shouldPatchNext =
      uiTime(nextTimeSlot.start_at) !== "00:00" || uiTime(nextTimeSlot.end_at) !== uiTime(end);

    if (shouldPatchNext) {
      await updateTimeSlot(token, nextTimeSlot.id, { start_at: "00:00", end_at: end });
    }

    // Sync breaks on next with b2
    await syncBreaks(token, nextTimeSlot.id, mapApiBreaksToUi(nextTimeSlot.breaks || []), b2);
  } else {
    // Need to create next piece on next day
    const nextWeekDay = anchorWeekDay === 7 ? 1 : anchorWeekDay + 1;
    const nextSbwExisting = getSbwForWeekDay(weeklyWorkingHour, nextWeekDay);

    const ensuredNextSbw = await ensureSbw(token, weeklyWorkingHour.id, nextWeekDay, nextSbwExisting);
    const nextSbwCustom = await detachWorkingHourIfNeeded(token, ensuredNextSbw);

    await createTimeSlotOnSbw(token, nextSbwCustom.id, {
      start_at: "00:00",
      end_at: end,
      breaks: normalizeBreaksForApi(b2),
    });
  }

  await onSaved?.();
  return { ok: true };
}


/**
 * Ritorna gli slot che si sovrappongono con [start,end) all’interno di una lista di time_slots
 * start/end sono "HH:MM" o "24:00"
 */
export function findOverlapsInDay(timeSlots = [], start, end, ignoreTimeSlotId = null) {
  const s = toMin(start);
  const e = toMin(end);
  if (s == null || e == null) return [];

  return (timeSlots || []).filter((ts) => {
    if (!ts?.id) return false;
    if (ignoreTimeSlotId && ts.id === ignoreTimeSlotId) return false;

    const tsS = toMin(String(ts.start_at).slice(0, 5));
    const tsE = toMin(String(ts.end_at).slice(0, 5));
    if (tsS == null || tsE == null) return false;

    return overlaps(s, e, tsS, tsE);
  });
}

/**
 * Mostra conferma sovrascrittura se ci sono overlap.
 * Ritorna boolean.
 */
export async function confirmOverwriteIfNeeded(overlapsList, dayLabel) {
  if (!overlapsList?.length) return true;

  const details = overlapsList
    .map((t) => `${String(t.start_at).slice(0, 5)}-${String(t.end_at).slice(0, 5)}`)
    .join(", ");

  const res = await Swal.fire({
    icon: "warning",
    title: "Sovrapposizione",
    html: `Stai creando/modificando una fascia che si sovrappone a: <b>${details}</b> (${dayLabel}).<br/>Vuoi sovrascrivere?`,
    showCancelButton: true,
    confirmButtonText: "Sovrascrivi",
    cancelButtonText: "Annulla",
    cancelButtonColor: "#d1d5db",
  });

  return !!res.isConfirmed;
}

/**
 * Cancella tutti i time_slots indicati (id) chiamando deleteTimeSlotCompany
 */
export async function deleteOverlappingSlots({ token, overlapsList, deleteTimeSlotCompany }) {
  for (const ts of overlapsList || []) {
    await deleteTimeSlotCompany(token, ts.id);
  }
}


// -----------------------
// SweetAlert wrapper for errors (nested errors too)
// -----------------------
export async function showApiError(e) {
  console.error(e);
  await Swal.fire({ icon: "error", title: "Errore", text: getUiErrorMessage(e) });
}